import { useMemo, useState } from 'react';

import clsx from 'clsx';
import { useDebounce } from 'use-debounce';
import { useQuery } from 'react-query';

import { Combobox } from '@headlessui/react';
import { ChevronUpDownIcon, XMarkIcon } from '@heroicons/react/24/outline';

import ThemeColorOptions from '../../types/system/ThemeColorOption';
import { LoadingWheel } from './LoadingWheel';
import useApiHelper from '../../hooks/useApiHelper';
import useQSBuilder from '../../hooks/useQSBuilder';
import { IDefaultResponseWithData } from '../../types/system/DefaultResponse';

export interface SearchableSelectOption {
  value: string;
  label: string;
  longDesc?: string;
}

interface SearchableSelectProps<T extends SearchableSelectOption> {
  value?: T | null;
  onChange: (value: T | null) => void;
  url: string;
  placeholder?: string;
  label?: string;
  disabled?: boolean;
  required?: boolean;
  small?: boolean;
  color?: ThemeColorOptions;
  className?: string;
  queryKey: string[];
  clearable?: boolean;
  queryParam?: string;
  transformResponse?: (data: any) => T[];
  containerClasses?: string;
}

export function SearchableSelect<T extends SearchableSelectOption>({
  value,
  onChange,
  url,
  placeholder = 'Search...',
  label,
  disabled = false,
  required = false,
  small = false,
  color = 'primary',
  className,
  queryKey,
  clearable = false,
  queryParam = 'query',
  transformResponse,
  containerClasses,
}: SearchableSelectProps<T>) {
  const [query, setQuery] = useState('');
  const [debouncedQuery] = useDebounce(query, 300);
  const { get } = useApiHelper();
  const buildQS = useQSBuilder();

  const { data: options = [], isLoading } = useQuery({
    queryKey: [...queryKey, { search: debouncedQuery }],
    queryFn: async () => {
      if (!debouncedQuery) return [];

      const queryString = buildQS({ [queryParam]: debouncedQuery });
      const response = await get<IDefaultResponseWithData<T[]>>(
        `${url}${queryString}`
      );

      if (transformResponse) {
        return transformResponse(
          response.data.data
        ) as SearchableSelectOption[];
      }

      // Default assumption: response.data is an array of options
      return (
        (response.data?.data as unknown as SearchableSelectOption[]) ??
        ([] as SearchableSelectOption[])
      );
    },
    enabled: debouncedQuery.length > 0,
  });

  const filteredOptions = useMemo(() => {
    if (!debouncedQuery) return [];
    return options;
  }, [options, debouncedQuery]);

  const handleClear = () => {
    onChange(null);
    setQuery('');
  };

  return (
    <div className={`flex flex-col space-y-2 ${containerClasses}`}>
      {label && (
        <label className="text-sm font-medium opacity-80">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}
      <Combobox
        as="div"
        value={value}
        onChange={onChange}
        disabled={disabled}
        by={(a, b) => a?.value === b?.value}
      >
        <div className="relative">
          <Combobox.Input
            className={clsx(
              'w-full rounded bg-white border ring-0 focus:ring-2 outline-0 focus:outline-0 dark:bg-gray-700 dark:text-white',
              color === 'primary'
                ? '!border-primary !ring-primary'
                : '!border-secondary !ring-secondary',
              disabled && 'opacity-50 cursor-not-allowed',
              small ? 'px-1 py-0.5 pr-10' : 'h-[42px] px-3 py-2 pr-10',
              className
            )}
            placeholder={placeholder}
            displayValue={(option: T | null) => option?.label || ''}
            onChange={(e) => setQuery(e.currentTarget.value)}
          />
          <div className="absolute inset-y-0 right-0 flex items-center pr-2 space-x-1">
            {clearable && value && (
              <XMarkIcon
                className="h-5 w-5 text-gray-400 hover:text-gray-600 cursor-pointer"
                onClick={handleClear}
              />
            )}
            <Combobox.Button className="flex items-center">
              {isLoading ? (
                <LoadingWheel className="h-5 w-5" />
              ) : (
                <ChevronUpDownIcon className="h-5 w-5 text-gray-400" />
              )}
            </Combobox.Button>
          </div>
          <Combobox.Options
            className={clsx(
              'absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded bg-white py-1 text-base shadow-lg ring-1 focus:outline-none sm:text-sm dark:bg-gray-700 dark:text-white',
              color === 'primary' ? '!ring-primary' : '!ring-secondary'
            )}
          >
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option) => (
                <Combobox.Option key={option.value} value={option}>
                  {({ active, selected }) => (
                    <li
                      className={clsx(
                        'group relative cursor-default select-none py-2 pl-3 pr-9',
                        selected && 'bg-primary text-primaryContrast',
                        active && 'bg-primary/80 text-primaryContrast'
                      )}
                    >
                      {option.label}
                      {option.longDesc && (
                        <p
                          className={clsx(
                            'text-sm text-gray-500 dark:text-gray-400',
                            selected || active ? 'text-primaryContrast/90' : ''
                          )}
                        >
                          {option.longDesc}
                        </p>
                      )}
                    </li>
                  )}
                </Combobox.Option>
              ))
            ) : query === '' ? (
              <div className="group relative cursor-default select-none opacity-80 italic text-center py-2 pl-3 pr-9">
                Type to search...
              </div>
            ) : isLoading ? (
              <div className="group relative cursor-default select-none opacity-80 italic text-center py-2 pl-3 pr-9">
                Searching...
              </div>
            ) : (
              <div className="group relative cursor-default select-none opacity-80 italic text-center py-2 pl-3 pr-9">
                No results found
              </div>
            )}
          </Combobox.Options>
        </div>
      </Combobox>
    </div>
  );
}
